/**
 * Frontend single source of truth for a listing's transfer status.
 *
 * A transfer has exactly one status at any time, stored in
 * `listing.attributes.metadata.transferStatus`:
 *   none      - the listing was never offered for transfer.
 *   pending   - staff sent a transfer invite, waiting for the invited person to pay.
 *   cancelled - staff cancelled the invite before anyone paid.
 *   completed - the invited person paid (set only by the Stripe webhook).
 *
 * Before this field existed the status was guessed from two unrelated things
 * (is the listing closed, and is `linkedUserEmail` set), which could drift out
 * of sync. `resolveTransferStatus` still applies that guess as a fallback for
 * listings written before the field existed, but every new write must set
 * `transferStatus` explicitly.
 *
 * NOTE: the server has its own CommonJS copy at `server/api-util/listingTransfer.js`
 * (it can't require across the frontend boundary). Keep the two in sync.
 */
export const TRANSFER_STATUS_NONE = 'none';
export const TRANSFER_STATUS_PENDING = 'pending';
export const TRANSFER_STATUS_CANCELLED = 'cancelled';
export const TRANSFER_STATUS_COMPLETED = 'completed';

/**
 * Reads `transferStatus` from a listing's metadata.
 *
 * @param {Object} params.metadata listing metadata (`transferStatus`, `linkedUserEmail`)
 * @param {Boolean} params.isClosed whether the listing is in the closed state
 * @returns {String} one of the TRANSFER_STATUS_* values
 */
export const resolveTransferStatus = ({ metadata = {}, isClosed = false } = {}) => {
  const { transferStatus, linkedUserEmail } = metadata || {};

  if (transferStatus) {
    // A closed listing has a paying owner, so a transfer on it cannot still be
    // waiting for payment. This pair only appears when the Stripe webhook failed
    // to write `completed`. Resolving it here stops staff from cancelling - and
    // so re-opening the claim button on - a transfer somebody already paid for.
    if (isClosed && transferStatus === TRANSFER_STATUS_PENDING) {
      return TRANSFER_STATUS_COMPLETED;
    }
    return transferStatus;
  }

  // Fallback for listings written before `transferStatus` existed. Old data only.
  if (isClosed) {
    return TRANSFER_STATUS_COMPLETED;
  }
  return linkedUserEmail ? TRANSFER_STATUS_PENDING : TRANSFER_STATUS_NONE;
};

/**
 * The public "Claim this listing" button may only show while no transfer is in
 * flight or done, and while the listing has no active paying owner (a closed
 * listing means someone already took it over).
 */
export const canClaimListing = ({ transferStatus, isClosed = false } = {}) =>
  !isClosed &&
  (transferStatus === TRANSFER_STATUS_NONE || transferStatus === TRANSFER_STATUS_CANCELLED);
